import React from 'react';
import { Link } from 'react-router-dom';
import { Battery, PenTool, CreditCard, GraduationCap, ArrowRight, Zap, Wrench, Bike, Shield } from 'lucide-react';
import SectionLabel from '../ui/SectionLabel';
import { useSection } from '../../hooks/useSection';

const ICONS: Record<string, React.ComponentType<{ className?: string; strokeWidth?: number }>> = { Battery, PenTool, CreditCard, GraduationCap, Zap, Wrench, Bike, Shield };

interface ServiceCardProps {
  icon: React.ComponentType<{ className?: string; strokeWidth?: number }>;
  title: string;
  description: string;
  linkUrl: string;
}

const ServiceCard: React.FC<ServiceCardProps> = ({ icon: Icon, title, description, linkUrl }) => (
  <Link
    to={linkUrl}
    className="group block shrink-0 snap-center w-[78%] md:w-auto md:shrink rounded-3xl bg-white p-6 ring-1 ring-neutral-200/80 shadow-[0_8px_30px_-15px_rgba(0,0,0,0.08)] transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_18px_45px_-15px_rgba(0,0,0,0.14)] sm:p-7"
  >
    <div className="mb-5 inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-primary-50 ring-1 ring-primary-100 transition-transform duration-500 group-hover:scale-110">
      <Icon className="h-5 w-5 text-primary-600" strokeWidth={2.5} />
    </div>
    <h3 className="mb-2 text-lg font-black tracking-tight text-neutral-900 sm:text-xl">{title}</h3>
    <p className="mb-5 text-sm leading-relaxed text-neutral-600">{description}</p>
    <span className="inline-flex items-center gap-1 text-sm font-bold text-neutral-900 transition-colors group-hover:text-primary-600">
      En savoir plus
      <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
    </span>
  </Link>
);

const DEFAULT_SERVICES = [
  { icon: 'Battery', title: 'Stations de recharge', description: 'Réseau Proxy Power — recharge rapide et fiable pour votre véhicule électrique.', linkUrl: '/stations' },
  { icon: 'PenTool', title: 'Maintenance & SAV', description: 'Service après-vente professionnel et maintenance préventive pour garder votre moto en parfait état.', linkUrl: '/services#maintenance' },
  { icon: 'CreditCard', title: 'Leasing & financement', description: 'Solutions de financement flexibles — PAYGO, PAY MOLO et paiement comptant.', linkUrl: '/pay-as-you-go' },
  { icon: 'GraduationCap', title: 'Proxy Academy', description: 'Formations professionnelles pour la conduite et la maintenance des motos électriques.', linkUrl: '/services#academy' },
];

const ServicesEcosystem: React.FC = () => {
  const { section } = useSection('home_services_ecosystem');
  if (section && section.is_visible === false) return null;
  const eyebrow = section?.subtitle || "L'écosystème Proxy";
  const intro = section?.content || 'Une plateforme complète pour accélérer votre transition vers la mobilité électrique — de la recharge à la formation.';
  const services = (section?.extra_data?.services?.length ? section.extra_data.services : DEFAULT_SERVICES) as Array<{ icon?: string; title: string; description: string; linkUrl: string }>;
  const btnLabel = section?.button_label || 'Découvrir tous nos services';
  const btnUrl = section?.button_url || '/services';

  return (
    <section className="relative overflow-hidden bg-gradient-to-b from-white to-neutral-50/60 py-14 sm:py-20 lg:py-24">
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -top-40 right-[8%] h-96 w-96 rounded-full bg-primary-500/[0.05] blur-3xl" />
        <div className="absolute bottom-0 left-[10%] h-[28rem] w-[28rem] rounded-full bg-neutral-900/[0.03] blur-3xl" />
      </div>
      <div className="container relative mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mx-auto mb-12 max-w-2xl text-center sm:mb-16">
          <SectionLabel number="08" className="mb-4">
            {eyebrow}
          </SectionLabel>
          <h2 className="mb-4 text-3xl font-black leading-[1.05] tracking-[-0.025em] text-neutral-900 sm:text-4xl lg:text-5xl">
            {section?.title ? section.title : (<>Nos <span className="text-primary-600">services</span></>)}
          </h2>
          <p className="mx-auto max-w-xl text-base leading-relaxed text-neutral-600 sm:text-lg">
            {intro}
          </p>
        </div>
        <div
          className="flex gap-3 overflow-x-auto pb-4 scrollbar-hide snap-x snap-mandatory -mx-4 px-4 md:mx-0 md:px-0 md:grid md:gap-4 md:grid-cols-2 lg:grid-cols-4 md:overflow-visible md:pb-0"
          style={{ scrollPaddingLeft: '1rem' }}
        >
          {services.map((service, t) => (
            <ServiceCard key={t} icon={(service.icon && ICONS[service.icon]) || Battery} title={service.title} description={service.description} linkUrl={service.linkUrl} />
          ))}
        </div>
        <div className="mt-12 text-center">
          <Link
            to={btnUrl}
            className="group inline-flex items-center justify-center gap-2 rounded-xl bg-neutral-900 px-6 py-3.5 text-sm font-bold text-white shadow-[0_14px_30px_-12px_rgba(0,0,0,0.45)] transition-all duration-300 hover:-translate-y-0.5 hover:bg-primary-600 hover:shadow-[0_18px_40px_-12px_rgba(24,153,78,0.55)]"
          >
            {btnLabel}
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default ServicesEcosystem;
